<script setup lang="ts">
import type { InputPrimaryEvent } from '@shared/ui/fields'

import { faTrashCan } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { DropZoneArea } from '@shared/ui/other'
import { InputPrimary } from '@shared/ui/fields'
import { BtnIcon } from '@shared/ui/buttons'

import { useAudioUploader, ASSESSMENT_AUDIO_ACCEPT } from '../../../model/assessments/audio-uploader.hook'

/**
 * One audio asset: drop a file in or paste a URL, and play what you chose.
 *
 * Owns the document's `asset.url` and emits the replacement, the way `ImageUploader` does: an
 * upload writes the object path, a pasted URL is kept as is. The drop area shows only while the
 * field is empty — replacing is Remove then drop again.
 */
defineOptions({
  name: 'AudioUploader',
})

const props = withDefaults(defineProps<{
  value?: string
}>(), {
  value: '',
})

const emit = defineEmits<{
  'update:value': [value: string]
}>()

const {
  refSrc,
  refIsResolving,
  refHasValue,
  refExternalUrl,
  refIsUploading,
  refProgress,
  refError,
  refCanUpload,
  handleFiles,
  handleUrlChange,
  handleRemove,
} = useAudioUploader({
  value: () => props.value,
  onChange: value => emit('update:value', value),
})
</script>

<template>
  <div class="audio-uploader">
    <div
      v-if="refHasValue"
      class="audio-uploader__preview"
    >
      <audio
        v-if="refSrc"
        class="audio-uploader__player"
        :src="refSrc"
        :title="value"
        controls
        preload="metadata"
      />

      <AppText
        v-else
        class="audio-uploader__player"
        name="body2"
        color="slate-500"
      >
        {{ refIsResolving ? 'Loading audio…' : 'No preview' }}
      </AppText>

      <BtnIcon
        :icon="faTrashCan"
        size="small"
        color="red"
        title="Remove audio"
        :disabled="refIsUploading"
        @click="handleRemove"
      />
    </div>

    <DropZoneArea
      v-else
      title="Drag & Drop an audio file here"
      browse-text="Browse audio"
      :accept="ASSESSMENT_AUDIO_ACCEPT"
      :multiple="false"
      :disabled="refIsUploading || !refCanUpload"
      @files="handleFiles"
    />

    <AppText
      v-if="refIsUploading"
      name="body2"
      color="slate-500"
    >
      Uploading… {{ refProgress }}%
    </AppText>

    <AppText
      v-if="!refCanUpload && !refHasValue"
      name="body2"
      color="slate-500"
    >
      Save the assessment template before uploading audio.
    </AppText>

    <AppText
      v-if="refError"
      name="body2"
      color="red-500"
    >
      {{ refError }}
    </AppText>

    <!-- Audio hosted elsewhere stays a valid asset. Empty for an uploaded object, whose path is not
         something to hand-edit. -->
    <InputPrimary
      name="audioUrl"
      label="…or paste an audio URL"
      optional-label="optional"
      :value="refExternalUrl"
      :disabled="refIsUploading"
      placeholder="https://…"
      @change="(event: InputPrimaryEvent) => handleUrlChange(String(event.value))"
    />
  </div>
</template>

<style scoped lang="scss" src="./AudioUploader.scss" />
